import { apiFetch, apiFetchBlob, apiFetchText } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import type {
  DailyQuotation,
  EffectiveQuotation,
  EffectiveQuotationItems,
  EnrichedQuotationItem,
  SupplierSubmissionItem,
  QuotationItem,
  CreateQuotationPayload,
  CreateSpecialQuotationPayload,
  UpdateQuotationPayload,
  CreateItemPayload,
  UpdateItemPayload,
  BootstrapPayload,
  PageResponse,
  QuotationExportDocument,
  ClientFacingQuotation,
  QuotationAssignmentIds,
  QuotationAssignmentPage,
  QuotationAssignmentQuery,
  QuotationHistoryItem,
} from "./types";

export function listQuotations(
  limit = 2000,
  offset = 0,
): Promise<PageResponse<QuotationHistoryItem>> {
  return apiFetch(
    `${ENDPOINTS.quotations.list}?limit=${limit}&offset=${offset}`,
  );
}

export async function findQuotationByDate(date: string, excludeId?: string) {
  const limit = 1000;
  let offset = 0;
  for (let page = 0; page < 100; page++) {
    const response = await listQuotations(limit, offset);
    const match = response.items.find(
      (quotation) =>
        !quotation.is_special &&
        quotation.id !== excludeId &&
        quotation.date.slice(0, 10) === date,
    );
    if (match) return match;
    if (
      response.items.length === 0 ||
      offset + response.items.length >= response.total
    ) {
      return null;
    }
    offset += Math.max(response.items.length, 1);
  }
  return null;
}

export function listAvailablePublications(
  signal?: AbortSignal,
): Promise<ClientFacingQuotation[]> {
  return apiFetch(ENDPOINTS.quotations.availablePublications, { signal });
}

export function listQuotationAssignments(
  quotationId: string,
  query: QuotationAssignmentQuery,
  signal?: AbortSignal,
): Promise<QuotationAssignmentPage> {
  const search = new URLSearchParams({
    limit: String(query.limit),
    offset: String(query.offset),
    search: query.search.trim(),
    assignment: query.assignment,
  });
  return apiFetch(
    `${ENDPOINTS.quotations.assignments(quotationId)}?${search.toString()}`,
    { signal },
  );
}

export function listQuotationAssignmentIds(
  quotationId: string,
  query: Pick<QuotationAssignmentQuery, "search" | "assignment">,
  signal?: AbortSignal,
): Promise<QuotationAssignmentIds> {
  const search = new URLSearchParams({
    search: query.search.trim(),
    assignment: query.assignment,
  });
  return apiFetch(
    `${ENDPOINTS.quotations.assignmentIds(quotationId)}?${search.toString()}`,
    { signal },
  );
}

export function replaceQuotationAssignments(
  quotationId: string,
  clientIds: string[],
): Promise<DailyQuotation> {
  return apiFetch(ENDPOINTS.quotations.assignments(quotationId), {
    method: "PUT",
    body: JSON.stringify({ client_ids: clientIds }),
  });
}

export function activateQuotationForAllClients(
  quotationId: string,
): Promise<DailyQuotation> {
  return apiFetch(ENDPOINTS.quotations.activateAllAssignments(quotationId), {
    method: "POST",
  });
}

export function deactivateQuotationForAllClients(
  quotationId: string,
): Promise<DailyQuotation> {
  return apiFetch(ENDPOINTS.quotations.deactivateAllAssignments(quotationId), {
    method: "POST",
  });
}

export function deactivateAllQuotationPublications(): Promise<void> {
  return apiFetch(ENDPOINTS.quotations.deactivateAllPublications, {
    method: "POST",
  });
}

export function getQuotation(id: string): Promise<DailyQuotation> {
  return apiFetch(ENDPOINTS.quotations.detail(id));
}

export function listSpecialQuotations(
  parentId: string,
): Promise<DailyQuotation[]> {
  return apiFetch(ENDPOINTS.quotations.specials(parentId));
}

export function getEffectiveQuotation(params: {
  date?: string;
  storeId?: string;
  clientId?: string;
}): Promise<EffectiveQuotation> {
  const search = new URLSearchParams();
  if (params.date) search.set("date", params.date);
  if (params.storeId) search.set("store_id", params.storeId);
  if (params.clientId) search.set("client_id", params.clientId);
  const query = search.toString();
  return apiFetch(
    query
      ? `${ENDPOINTS.quotations.effective}?${query}`
      : ENDPOINTS.quotations.effective,
  );
}

export function getEffectiveQuotationItems(params: {
  date?: string;
  storeId?: string;
  clientId?: string;
  limit?: number;
  offset?: number;
}): Promise<EffectiveQuotationItems> {
  const search = new URLSearchParams({
    limit: String(params.limit ?? 2000),
    offset: String(params.offset ?? 0),
  });
  if (params.date) search.set("date", params.date);
  if (params.storeId) search.set("store_id", params.storeId);
  if (params.clientId) search.set("client_id", params.clientId);
  return apiFetch(
    `${ENDPOINTS.quotations.effectiveItems}?${search.toString()}`,
  );
}

export function getQuotationExport(id: string): Promise<string> {
  return apiFetchText(ENDPOINTS.quotations.export(id));
}

export function getQuotationExportData(
  id: string,
): Promise<QuotationExportDocument> {
  return apiFetch(ENDPOINTS.quotations.exportData(id));
}

export function downloadQuotationExportPdf(
  id: string,
): Promise<{ blob: Blob; filename?: string }> {
  return apiFetchBlob(ENDPOINTS.quotations.exportPdf(id));
}

export function createQuotation(
  payload: CreateQuotationPayload,
): Promise<DailyQuotation> {
  return apiFetch(ENDPOINTS.quotations.list, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createSpecialQuotation(
  parentId: string,
  payload: CreateSpecialQuotationPayload,
): Promise<DailyQuotation> {
  return apiFetch(ENDPOINTS.quotations.specials(parentId), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateSpecialQuotationTargets(
  quotationId: string,
  payload: CreateSpecialQuotationPayload,
): Promise<DailyQuotation> {
  return apiFetch(ENDPOINTS.quotations.targets(quotationId), {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function updateQuotation(
  id: string,
  payload: UpdateQuotationPayload,
): Promise<DailyQuotation> {
  return apiFetch(ENDPOINTS.quotations.detail(id), {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteQuotation(id: string): Promise<void> {
  return apiFetch(ENDPOINTS.quotations.detail(id), { method: "DELETE" });
}

export function listQuotationItems(
  quotationId: string,
  limit = 2000,
  offset = 0,
  storeId?: string,
): Promise<PageResponse<EnrichedQuotationItem>> {
  const search = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (storeId) search.set("store_id", storeId);
  return apiFetch(
    `${ENDPOINTS.quotations.items(quotationId)}?${search.toString()}`,
  );
}

export function listQuotationSubmissions(
  date: string,
  limit = 2000,
  offset = 0,
): Promise<PageResponse<SupplierSubmissionItem>> {
  return apiFetch(
    `${ENDPOINTS.quotations.submissions}?date=${date}&limit=${limit}&offset=${offset}`,
  );
}

export function createQuotationItem(
  quotationId: string,
  payload: CreateItemPayload,
): Promise<QuotationItem> {
  return apiFetch(ENDPOINTS.quotations.items(quotationId), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateQuotationItem(
  quotationId: string,
  itemId: string,
  payload: UpdateItemPayload,
): Promise<QuotationItem> {
  return apiFetch(ENDPOINTS.quotations.item(quotationId, itemId), {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteQuotationItem(
  quotationId: string,
  itemId: string,
): Promise<void> {
  return apiFetch(ENDPOINTS.quotations.item(quotationId, itemId), {
    method: "DELETE",
  });
}

export function bootstrapQuotation(
  sourceId: string,
  payload: BootstrapPayload,
): Promise<DailyQuotation> {
  return apiFetch(ENDPOINTS.quotations.bootstrap(sourceId), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function copyIntoQuotation(
  sourceId: string,
  targetId: string,
): Promise<DailyQuotation> {
  return apiFetch(ENDPOINTS.quotations.copyInto(sourceId, targetId), {
    method: "POST",
  });
}

export function copySpecialIntoQuotation(
  sourceId: string,
  specialId: string,
  targetId: string,
): Promise<DailyQuotation> {
  return apiFetch(
    ENDPOINTS.quotations.copySpecialInto(sourceId, specialId, targetId),
    { method: "POST" },
  );
}
